import { useEffect, useRef, MouseEvent, forwardRef, useImperativeHandle, useState } from "react"
import { Magnifier, MagnifierData, MagnifierHandle } from "./Magnifier"
import { createPortal } from "react-dom"
import { convertHexStringToRGBArray } from "@nova/util-strings"

interface Props {
    file: File
    onColorSelect: (hex: string) => void
}

export const ImageColorSampler = ({ file, onColorSelect }: Props) => {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const loupeRef = useRef<ColorLoupeHandle>(null)

    useEffect(() => {
        if (!canvasRef.current || !file) return
        const img = new Image()
        const url = URL.createObjectURL(file)

        img.onload = () => {
            const cvs = canvasRef.current!

            cvs.width = img.width
            cvs.height = img.height

            const ctx = cvs.getContext("2d", { willReadFrequently: true })
            if (ctx) {
                ctx.imageSmoothingEnabled = false
                ctx.drawImage(img, 0, 0, cvs.width, cvs.height)
            }

            URL.revokeObjectURL(url)
        }
        img.src = url
    }, [file])

    const handleMouseMove = (e: MouseEvent<HTMLCanvasElement>) => {
        const canvas = canvasRef.current
        const ctx = canvas?.getContext("2d")
        if (!canvas || !ctx || !loupeRef.current) return

        const rect = canvas.getBoundingClientRect()

        const scaleX = canvas.width / rect.width
        const scaleY = canvas.height / rect.height

        const x = (e.clientX - rect.left) * scaleX
        const y = (e.clientY - rect.top) * scaleY

        const hex = sampleColor(ctx, x, y)

        loupeRef.current.update({
            screenX: e.clientX,
            screenY: e.clientY,
            pixelX: x,
            pixelY: y,
            sourceCanvas: canvas,
            color: hex,
        })
    }

    const onCanvasClick = (e: MouseEvent<HTMLCanvasElement>) => {
        const canvas = canvasRef.current
        if (!canvas) return
        const rect = canvas.getBoundingClientRect()
        const scaleX = canvas.width / rect.width
        const scaleY = canvas.height / rect.height
        const x = (e.clientX - rect.left) * scaleX
        const y = (e.clientY - rect.top) * scaleY
        const hex = sampleColor(canvas.getContext("2d")!, x, y)
        onColorSelect(hex)
    }

    return (
        <div className="flex h-full w-full items-center justify-center">
            <canvas
                ref={canvasRef}
                className="block max-w-full max-h-full object-contain"
                onMouseMove={handleMouseMove}
                onMouseLeave={() => loupeRef.current?.hide()}
                onClick={onCanvasClick}
                style={{
                    cursor: 'url("/images/cursors/cursor-eyedrop.svg") 0 32, auto',
                }}
            />
            <ColorSampleLoupe ref={loupeRef} />
        </div>
    )
}

const sampleColor = (ctx: CanvasRenderingContext2D, x: number, y: number) => {
    try {
        const { data } = ctx.getImageData(x, y, 1, 1)
        const r = data[0]
        const g = data[1]
        const b = data[2]
        const toHex = (n: number) => Math.floor(n).toString(16).padStart(2, "0")

        return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase()
    } catch {
        return "#000000"
    }
}
export interface ColorLoupeData extends MagnifierData {
    color: string
    screenX: number
    screenY: number
}

export interface ColorLoupeHandle {
    update: (data: ColorLoupeData) => void
    hide: () => void
}

export const ColorSampleLoupe = forwardRef<ColorLoupeHandle>((_, ref) => {
    const magnifierRef = useRef<MagnifierHandle>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const [currentColor, setCurrentColor] = useState<string>("#000000") // Default safe color

    useImperativeHandle(ref, () => ({
        update: (data: ColorLoupeData) => {
            if (containerRef.current) {
                containerRef.current.style.display = "flex"
                const offsetX = 16
                const offsetY = 16

                containerRef.current.style.transform = `translate(${data.screenX + offsetX}px, ${data.screenY + offsetY}px)`
            }

            magnifierRef.current?.update({
                pixelX: Math.floor(data.pixelX),
                pixelY: Math.floor(data.pixelY),
                sourceCanvas: data.sourceCanvas,
            })

            setCurrentColor(data.color)
        },
        hide: () => {
            if (containerRef.current) containerRef.current.style.display = "none"
            magnifierRef.current?.hide()
        },
    }))

    const [r, g, b] = convertHexStringToRGBArray(currentColor || "#000000")
    return createPortal(
        <div
            ref={containerRef}
            className="pointer-events-none fixed z-[9999] flex flex-col items-center gap-2 will-change-transform top-0 left-0"
            style={{ display: "none" }}
        >
            <div className="rounded bg-gray-700 text-white p-3 shadow-xl">
                <div className="flex gap-2.5">
                    <Magnifier
                        ref={magnifierRef}
                        zoom={12}
                        size={60}
                        className="rounded-full border-2 border-white"
                    />
                    <div className="flex flex-col flex-1 justify-between">
                        <div
                            className="h-5 rounded-md border"
                            style={{
                                backgroundColor: currentColor,
                                borderColor: `rgb(${Math.max(r - 25, 0)},${Math.max(g - 25, 0)},${Math.max(b - 25, 0)})`,
                            }}
                        />
                        <div className="flex gap-4">
                            <h4 className="text-gray-400">R</h4>
                            <h4>{r}</h4>
                            <h4 className="text-gray-400">G</h4>
                            <h4>{g}</h4>
                            <h4 className="text-gray-400">B</h4>
                            <h4>{b}</h4>
                        </div>
                        <div className="flex items-center gap-1">
                            <h4 className="text-gray-400">#</h4>
                            <h4>{currentColor?.substring(1)}</h4>
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.getElementById("cursor-nova")!
    )
})

ColorSampleLoupe.displayName = "ColorSampleLoupe"
